import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { inputCls } from "@/components/AuthShell";
import { IdCard, CheckCircle2, AlertCircle, Loader2, Pencil, Save, X, Sparkles } from "lucide-react";

const FIELDS = [
  { key: "full_legal_name", label: "Full legal name", ph: "First Middle Last" },
  { key: "phone", label: "Phone", ph: "(555) 555-5555" },
  { key: "email", label: "Email", ph: "you@example.com" },
  { key: "home_address", label: "Home address", ph: "Street, City, State ZIP", full: true },
  { key: "dl_state", label: "Driver's license state", ph: "e.g. TX" },
  { key: "dl_number", label: "Driver's license number", ph: "DL number" },
  { key: "dob", label: "Date of birth", type: "date" },
  { key: "insurance_company", label: "Insurance company", ph: "e.g. State Farm" },
  { key: "policy_number", label: "Policy number", ph: "Policy #" },
  { key: "vehicle_year", label: "Vehicle year", ph: "2021" },
  { key: "vehicle_make", label: "Vehicle make", ph: "Toyota" },
  { key: "vehicle_model", label: "Vehicle model", ph: "Camry" },
  { key: "license_plate", label: "License plate", ph: "ABC-1234" },
  { key: "emergency_contact_name", label: "Emergency contact name", ph: "Full name" },
  { key: "emergency_contact_phone", label: "Emergency contact phone", ph: "(555) 555-5555" },
];

export default function DriverProfileCard({ welcome = false }) {
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const cardRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/driver-profile");
      setProfile(data);
      // Post-signup: auto-open the form and scroll to it so a new client can fill
      // out their profile immediately, before an emergency ever happens.
      if (welcome && !data.complete) {
        const f = {};
        FIELDS.forEach(({ key }) => { f[key] = data?.[key] || ""; });
        setForm(f);
        setEditing(true);
        setTimeout(() => cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
      }
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const startEdit = () => {
    const f = {};
    FIELDS.forEach(({ key }) => { f[key] = profile?.[key] || ""; });
    setForm(f);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/driver-profile", form);
      setProfile(data);
      setEditing(false);
      toast.success("Driver profile saved");
    } catch {
      toast.error("Could not save profile");
    } finally { setSaving(false); }
  };

  if (!profile) {
    return (
      <div className="mt-6 bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex items-center gap-3 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading Driver Profile…
      </div>
    );
  }

  const complete = profile.complete;
  const pct = profile.completion_pct;

  return (
    <div ref={cardRef} className={`mt-6 bg-white rounded-3xl border p-6 md:p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-shadow ${welcome && !complete ? "border-cobalt/40 ring-2 ring-cobalt/20" : "border-slate-100"}`} data-testid="driver-profile-card">
      {welcome && !complete && (
        <div data-testid="driver-profile-welcome" className="mb-5 rounded-2xl bg-navy text-white p-4 flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-cobalt-light mt-0.5 shrink-0" />
          <p className="text-sm">Welcome to Crash Ready! Complete your Driver Profile now so we can auto-fill your accident report in an emergency — you'll never scramble for details at the scene.</p>
        </div>
      )}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-cobalt-light flex items-center justify-center shrink-0">
            <IdCard className="w-6 h-6 text-cobalt" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-navy">Driver Profile</h2>
            <p className="text-sm text-slate-500 mt-0.5">Saved once, auto-filled into every accident report.</p>
          </div>
        </div>
        {complete ? (
          <span data-testid="driver-profile-badge-complete" className="shrink-0 flex items-center gap-1.5 text-xs font-semibold bg-green-50 text-green-600 px-3 py-1.5 rounded-full">
            <CheckCircle2 className="w-3.5 h-3.5" /> Complete
          </span>
        ) : (
          <span data-testid="driver-profile-badge-incomplete" className="shrink-0 flex items-center gap-1.5 text-xs font-semibold bg-amber-50 text-amber-600 px-3 py-1.5 rounded-full">
            <AlertCircle className="w-3.5 h-3.5" /> Incomplete
          </span>
        )}
      </div>

      {/* Progress */}
      <div className="mt-5">
        <div className="flex items-center justify-between text-xs font-medium text-slate-500 mb-1.5">
          <span>Profile completion</span>
          <span data-testid="driver-profile-pct" className="text-navy font-semibold">{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ${complete ? "bg-green-500" : "bg-amber-400"}`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      {!editing && !complete && (
        <div data-testid="driver-profile-prompt" className="mt-5 rounded-2xl bg-cobalt-light/60 border border-cobalt/10 p-4 text-sm text-cobalt">
          Complete your Driver Profile now so Crash Ready can auto-fill your accident report in an emergency.
        </div>
      )}

      {!editing ? (
        <>
          {complete && (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {FIELDS.map(({ key, label }) => (
                <div key={key} className={key === "home_address" ? "sm:col-span-2" : ""}>
                  <div className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</div>
                  <div className="text-navy font-medium mt-0.5 break-words">{profile[key] || "—"}</div>
                </div>
              ))}
            </div>
          )}
          <button onClick={startEdit} data-testid="driver-profile-edit-btn"
            className="mt-6 w-full sm:w-auto flex items-center justify-center gap-2 bg-navy text-white px-6 py-3.5 rounded-full font-semibold hover:-translate-y-0.5 transition-all active:scale-[0.99]">
            {complete ? <Pencil className="w-4 h-4" /> : null}
            {complete ? "Edit Driver Profile" : "Complete Driver Profile"}
          </button>
        </>
      ) : (
        <div className="mt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FIELDS.map(({ key, label, ph, type, full }) => (
              <div key={key} className={full ? "sm:col-span-2" : ""}>
                <label className="text-sm font-medium text-slate-700">{label}</label>
                <input
                  className={`${inputCls} mt-1.5 text-base`}
                  type={type || "text"}
                  value={form[key] || ""}
                  placeholder={ph}
                  data-testid={`driver-profile-input-${key}`}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                />
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-center gap-3">
            <button onClick={save} disabled={saving} data-testid="driver-profile-save-btn"
              className="flex items-center justify-center gap-2 bg-navy text-white px-6 py-3.5 rounded-full font-semibold hover:-translate-y-0.5 transition-all active:scale-[0.99] disabled:opacity-70">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Changes
            </button>
            <button onClick={() => setEditing(false)} disabled={saving} data-testid="driver-profile-cancel-btn"
              className="flex items-center gap-1.5 text-slate-500 font-medium px-4 py-3.5 hover:text-navy transition-colors">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
